import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router";
import useHashArray from "../../../../hooks/useHashArray";
import {
  getAllRolesActionHandler,
  getProductCategoryListActionHandler,
  getUserGroupsActionHandler,
  postUserProfileActionHandler,
  putUserProfileActionHandler,
} from "../../../../store/actions/actionHandlers/adminActionHandler/userManagementActionHandler";
import { userManagementForm } from "../../../../utils/mainTabs/admin/userManagement/userManagementForm";
import { addAdditionalDetailsSchema } from "../../../../utils/mainTabs/admin/userManagement/userManagementFormSchema";
import Button from "../../../Common/Button/Button";
import Input from "../../../Common/Input/Input";
import PreviousIcon from "../../../Common/SvgIcons/CommonIcons/PreviousIcon";
import classes from "./AddAdditionalDetails.module.scss";
import { FormattedMessage } from "react-intl";
import { setErrorHandler } from "../../../../store/actions/actionHandlers/errorActionHandler";
import { errorTypes } from "../../../../utils/errorTypes";
import Modal from "../../../Common/Modal/Modal";
import { getUserLoginFlags } from "./userLoginFlags";

const AddAdditionalDetails = () => {
  const webLearnerRole = "Web Learner";
  const mobileLearnerRole = "Mobile Learner";
  const superTenantAdminRole = "Super Tenant Admin";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const {
    formValues,
    userManagementPath,
    editUserPath,
    userLocationPath,
    customRadio,
    aadRadio,
    setFormValues,
    addUserPath,
  } = useOutletContext();
  const commonState = useSelector((state) => state.commonReducer);
  const { colors } = commonState;

  const [fields, setFields] = useState([]);
  const [userDepartmentList, setUserDepartmentList] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [userGroupList, setUserGroupList] = useState([]);

  const adminStateReducer = useSelector((state) => state.adminReducer);
  const { userManagementReducer } = adminStateReducer;
  const {
    getProductCategoryListLoader,
    getUserGroupsLoader,
    getAllRolesLoader,
    postUserProfileLoader,
    putUserProfileLoader,
  } = userManagementReducer;

  const methods = useForm({
    defaultValues: formValues,
    resolver: yupResolver(addAdditionalDetailsSchema),
  });

  const {
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
    getValues,
  } = methods;

  const { hashTable: categoryHashTable } = useHashArray(
    userDepartmentList,
    "CategoryId",
    false,
    true,
    "SubCategory"
  );
  const { hashTable: selectedCategoryHashTable } = useHashArray(
    formValues?.Category,
    "CategoryId",
    true,
    true,
    "SubCategory"
  );

  const { hashTable: selectedRoleHashTable } = useHashArray(
    formValues?.AssociatedRoles,
    "RoleID",
    true
  );

  const { hashTable: selectedUserGroupHashTable } = useHashArray(
    formValues?.UserGroupDetails,
    "UserGroupId",
    true
  );

  const {
    userLocation: { name: userLocationName },
    additionalDetails: { formFields },
  } = userManagementForm;

  const checkNestedList = useCallback(
    (data) => {
      let nestedCheckedData = data?.map((category) => {
        if (selectedCategoryHashTable[category?.CategoryId]) {
          return {
            ...category,
            checked: true,
            SubCategory: category?.SubCategory?.map((subData) => ({
              ...subData,
              checked: true,
            })),
          };
        } else if (category?.SubCategory?.length > 0) {
          const nestedCheckedData = checkNestedList(category?.SubCategory);
          return { ...category, SubCategory: nestedCheckedData };
        } else {
          return { ...category, checked: false };
        }
      });
      return nestedCheckedData;
    },
    [selectedCategoryHashTable]
  );

  useEffect(() => {
    if (
      params?.userId &&
      selectedCategoryHashTable &&
      selectedRoleHashTable &&
      selectedUserGroupHashTable
    ) {
      dispatch(getProductCategoryListActionHandler()).then((data) => {
        let updatedUserDepartmentList = checkNestedList(data);
        setUserDepartmentList(updatedUserDepartmentList);
      });
      dispatch(getAllRolesActionHandler()).then((data) => {
        let updatedRoleList = data?.map((role) => {
          return selectedRoleHashTable[role?.RoleID]
            ? role?.RoleName === webLearnerRole ||
              role?.RoleName === mobileLearnerRole
              ? { ...role, checked: true, disabled: true }
              : role?.RoleName === superTenantAdminRole
              ? { ...role, checked: true, disabled: true }
              : { ...role, checked: true }
            : role?.RoleName === webLearnerRole ||
              role?.RoleName === mobileLearnerRole
            ? { ...role, checked: true, disabled: true }
            : role?.RoleName === superTenantAdminRole
            ? { ...role, checked: true, disabled: true }
            : role;
        });
        setRolesList(updatedRoleList);
      });
      dispatch(getUserGroupsActionHandler()).then((data) => {
        let UpdatedUserGroupList = data?.UserGroups?.map((userGroup) => {
          return selectedUserGroupHashTable[userGroup?.UserGroupId]
            ? userGroup.Name === "Default"
              ? { ...userGroup, checked: true, disabled: true }
              : { ...userGroup, checked: true }
            : userGroup.Name === "Default"
            ? { ...userGroup, disabled: true }
            : userGroup;
        });
        setUserGroupList(UpdatedUserGroupList);
      });
    }
  }, [
    dispatch,
    params?.userId,
    selectedCategoryHashTable,
    checkNestedList,
    selectedRoleHashTable,
    selectedUserGroupHashTable,
    reset,
  ]);
  useEffect(() => {
    if (!params?.userId) {
      dispatch(getProductCategoryListActionHandler()).then((data) => {
        setUserDepartmentList(data);
      });
      dispatch(getAllRolesActionHandler()).then((data) => {
        let updatedRoleList = data?.map((role) => {
          return role?.RoleName === superTenantAdminRole
            ? { ...role, disabled: true }
            : role;
        });
        setRolesList(updatedRoleList);
      });
      dispatch(getUserGroupsActionHandler()).then((data) => {
        setUserGroupList(data.UserGroups);
      });
    }
  }, [dispatch, params?.userId, reset]);

  useEffect(() => {
    const isSuperTenant =
      params?.userId &&
      formValues?.AssociatedRoles?.find(
        (role) => role?.RoleName === superTenantAdminRole
      )
        ? true
        : false;
    let formArgs = {
      userDepartmentList,
      setUserDepartmentList,
      getProductCategoryListLoader,
      rolesList,
      setRolesList,
      getAllRolesLoader,
      userGroupList,
      setUserGroupList,
      getUserGroupsLoader,
      isSuperTenant,
    };
    const fieldsList = formFields(formArgs);
    setFields(fieldsList);
  }, [
    params?.userId,
    selectedRoleHashTable,
    formFields,
    userDepartmentList,
    setUserDepartmentList,
    getProductCategoryListLoader,
    rolesList,
    setRolesList,
    getAllRolesLoader,
    userGroupList,
    setUserGroupList,
    getUserGroupsLoader,
  ]);
  const [userDepOpen, setUserDepOpen] = useState(false);
  const [submitData, setSubmitData] = useState({});
  const [confirmModal, setConfirmModal] = useState(false);
  const onSubmit = (data) => {
    const tenanatType = localStorage
      .getItem("SecurityProviderName")
      .toLowerCase();
    const tenantData = JSON.parse(localStorage.getItem("tenantData") || "{}");
    const isHybridIdentityTenant =
      tenantData?.SAMLConfig?.IdentityType === 2 &&
      ["saml2", "custom"].includes(tenanatType);
    setConfirmModal(true);
    const {
      City,
      State,
      TagSearch,
      Country,
      UserGroupDetails,
      Category,
      ...rest
    } = data;
    // const categoryHashTable = {};
    // const createCategoryHashTable = (category) => {
    //   categoryHashTable[category.CategoryId] = category;
    //   if (category?.SubCategory?.length > 0) {
    //     category?.SubCategory?.forEach((category) => {
    //       createCategoryHashTable(category);
    //     });
    //   }
    // };
    // userDepartmentList.forEach((category) => {
    //   createCategoryHashTable(category);
    // });
    let UpdatedCategory = Category.map(
      (category) => categoryHashTable[category.CategoryId]
    ).filter((category) => category !== undefined);

    let UpdatedUserGroupDetails = UserGroupDetails?.map((userGroup) => {
      return {
        Name: userGroup?.Name,
        UserGroupId: userGroup?.UserGroupId,
        isActive: false,
        isExpanded: false,
        isFiltered: false,
        selected: true,
      };
    });
    let UpdatedUserGroups = UserGroupDetails?.map((userGroup) => {
      if (location.pathname.includes(editUserPath)) {
        return { Name: userGroup?.Name, UserGroupId: userGroup?.UserGroupId };
      } else {
        return { Name: userGroup?.Name };
      }
    });

    let loginId =
      data?.DomainName?.length > 0 && !customRadio
        ? data?.LoginId + "" + data?.DomainName[0]?.Name
        : data?.LoginId;
    const loginFlags = getUserLoginFlags(
      tenanatType,
      customRadio,
      aadRadio,
      isHybridIdentityTenant
    );
    const userData = {
      ...rest,
      City: null,
      LoginId: loginId,
      ...loginFlags,
      Category: UpdatedCategory,
      UserGroupDetails: UpdatedUserGroupDetails,
      UserGroups: UpdatedUserGroups,
    };
    const a = delete userData.DomainName;

    setSubmitData(userData);
    if (location.pathname.includes(editUserPath)) {
      if (formValues.Category.length != Category.length) {
        setUserDepOpen(true);
      } else {
        dispatch(putUserProfileActionHandler(userData)).then((response) => {
          if (response) {
            navigate(userManagementPath);
          }
        });
      }
    } else {
      dispatch(postUserProfileActionHandler(userData)).then((response) => {
        if (response) {
          navigate(userManagementPath);
        }
      });
    }
  };
  const handleSubmitModal = (userData) => {
    dispatch(putUserProfileActionHandler(userData)).then((response) => {
      if (response) {
        navigate(userManagementPath);
      }
    });
  };
  const handleReset = () => {
    reset();
    if (location.pathname?.includes(addUserPath)) {
      let updateUserDepartmentList = userDepartmentList?.map((data) => {
        return {
          ...data,
          checked: false,
          SubCategory: data?.SubCategory?.map((subData) => ({
            ...subData,
            checked: false,
          })),
        };
      });
      setUserDepartmentList(updateUserDepartmentList);
      let updateRoleList = rolesList?.map((data) => {
        return { ...data, checked: false };
      });
      setRolesList(updateRoleList);
      let updateUserGroupList = userGroupList?.map((data) => {
        return { ...data, checked: false };
      });
      setUserGroupList(updateUserGroupList);
      // dispatch(getProductCategoryListActionHandler()).then((data) => {
      //   let updatedUserDepartmentList = checkNestedList(data);
      //   setUserDepartmentList(updatedUserDepartmentList);
      // });
    } else {
      dispatch(getProductCategoryListActionHandler()).then((data) => {
        let updatedUserDepartmentList = checkNestedList(data);
        setUserDepartmentList(updatedUserDepartmentList);
      });
      dispatch(getAllRolesActionHandler()).then((data) => {
        let updatedRoleList = data?.map((role) => {
          return selectedRoleHashTable[role?.RoleID]
            ? role?.RoleName === webLearnerRole ||
              role?.RoleName === mobileLearnerRole
              ? { ...role, checked: true, disabled: true }
              : role?.RoleName === superTenantAdminRole
              ? { ...role, checked: true, disabled: true }
              : { ...role, checked: true }
            : role?.RoleName === webLearnerRole ||
              role?.RoleName === mobileLearnerRole
            ? { ...role, checked: true, disabled: true }
            : role?.RoleName === superTenantAdminRole
            ? { ...role, checked: true, disabled: true }
            : role;
        });
        setRolesList(updatedRoleList);
      });
      dispatch(getUserGroupsActionHandler()).then((data) => {
        let UpdatedUserGroupList = data?.UserGroups?.map((userGroup) => {
          return selectedUserGroupHashTable[userGroup?.UserGroupId]
            ? userGroup.Name === "Default"
              ? { ...userGroup, checked: true, disabled: true }
              : { ...userGroup, checked: true }
            : userGroup.Name === "Default"
            ? { ...userGroup, disabled: true }
            : userGroup;
        });
        setUserGroupList(UpdatedUserGroupList);
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={classes.addAdditionalDetails__form}
      >
        <div className={classes.addAdditionalDetails__form__fields}>
          {fields.map((field) => {
            return (
              <Input
                key={field?.key}
                label={field?.label}
                name={field?.name}
                placeholder={field?.placeholder}
                labelColor={field?.labelColor}
                error={errors?.[field?.name]?.message}
                type={field?.type}
                fieldName={field?.fieldName}
                fieldValue={field?.fieldValue}
                height={field?.height}
                width={field?.width}
                isNested={field?.isNested}
                nestFieldName={field?.nestFieldName}
                searchTagsList={field?.searchTagsList}
                handleTagList={field?.handleTagList}
                disabled={field?.disabled}
                loading={field?.loading}
                searchable={field?.searchable}
                labelMsg={field?.labelMsg}
              />
            );
          })}
        </div>
        <div className={classes.addAdditionalDetails__form__footer}>
          <div
            className={classes.addAdditionalDetails__form__footer__backButton}
            onClick={() => {
              let additionalDetailsData = getValues();
              setFormValues({ ...formValues, ...additionalDetailsData });
              navigate(userLocationPath);
            }}
          >
            <PreviousIcon fill={colors.theme} />
            <div
              className={
                classes.addAdditionalDetails__form__footer__backButton__linkMedium
              }
            >
              {userLocationName}
            </div>
          </div>
          <div
            className={classes.addAdditionalDetails__form__footer__rightSection}
          >
            {(isDirty || isValid) && (
              <div
                className={
                  classes.addAdditionalDetails__form__footer__rightSection__clearAll
                }
                onClick={() => handleReset()}
              >
                <FormattedMessage id="CLEAR_ALL_TXT" />
              </div>
            )}
            <Button
              label={
                // location.pathname.includes(editUserPath) ? "Edit" :
                <FormattedMessage id="SAVE_TXT" />
              }
              small
              dark
              type="submit"
              disabled={
                !isValid || postUserProfileLoader || putUserProfileLoader
              }
              loading={
                postUserProfileLoader || (!userDepOpen && putUserProfileLoader)
              }
            />
          </div>
        </div>
        <Modal
          heading={"Shotclasses Message"}
          isOpen={userDepOpen}
          toggle={() => setUserDepOpen(false)}
          buttonLabel1="Yes"
          buttonLabel2="No"
          width={"40"}
          height={"25"}
          handleSubmit={() => {
            handleSubmitModal(submitData, setConfirmModal);
          }}
          loading={putUserProfileLoader}
        >
          <div className={classes.addAdditionalDetails__form__modal}>
            You are about to change the user’s existing associations and it may
            change future notifications and assignments. Do you want to Proceed
            ?
          </div>
        </Modal>
      </form>
    </FormProvider>
  );
};

export default AddAdditionalDetails;
